import React from "react";
import { Box, HStack, Text, VStack, IconButton } from "@chakra-ui/react";
import { FaRegTrashAlt } from "react-icons/fa";
import {
  DeliveryOption,
  SupplierPartForDelivery,
} from "../../types";
import { OrderPayableBy, OrderPayableTo, EstimateSplitFulfillmentStatus } from "../../../../views/Orders/enums";
import { estimateSectionHeaderProps } from "../../utils/estimateSectionHeaderStyles";
import { SplitConfigurationSection } from "./SplitConfigurationSection";

interface PartsListColumnProps {
  parts: SupplierPartForDelivery[];
  estimateNo: string;
  splitId: string;
  supplierId?: string | null;
  orderData?: any;
  orderNo?: string;
  garaazSupplierIdFromConfig?: string;
  isSingleGaraazInvoiceRequired?: boolean;
  isRemainingItems: boolean;
  transactionType?: string;
  discountPercentage?: number;
  creditNotePercentage?: number;
  fulfillmentStatus?: EstimateSplitFulfillmentStatus;
  paymentRouteFromLabel: string;
  paymentRouteToLabel: string;
  payableBy?: OrderPayableBy;
  payableTo?: OrderPayableTo;
  isExpired: boolean;
  onSplitConfigApplied: (update: {
    transactionType?: string;
    discountPercentage?: number;
    creditNotePercentage?: number;
    fulfillmentStatus?: EstimateSplitFulfillmentStatus;
    payableBy?: OrderPayableBy;
    payableTo?: OrderPayableTo;
    /** When omitted, existing delivery options on the split are preserved. */
    deliveryOptions?: DeliveryOption[];
  }) => void;
  isReadOnly?: boolean;
  canRemoveParts?: boolean;
  onRemovePart?: (partIndex: number) => void;
}

export const PartsListColumn: React.FC<PartsListColumnProps> = ({
  parts,
  estimateNo,
  splitId,
  supplierId,
  orderData,
  orderNo,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired,
  isRemainingItems,
  transactionType,
  discountPercentage,
  creditNotePercentage,
  fulfillmentStatus,
  paymentRouteFromLabel,
  paymentRouteToLabel,
  payableBy,
  payableTo,
  isExpired,
  onSplitConfigApplied,
  isReadOnly = false,
  canRemoveParts = false,
  onRemovePart,
}) => (
  <Box
    flex="1"
    px={4}
    py={3}
    borderRight={{ base: "none", md: "1px solid" }}
    borderBottom={{ base: "1px solid", md: "none" }}
    borderColor="gray.100"
    bg="gray.50"
  >
    <HStack mb={2} spacing={4}>
      <Text {...estimateSectionHeaderProps} flex="1">
        Part Details
      </Text>
      <Text {...estimateSectionHeaderProps} w="72px" textAlign="right">
        Qty
      </Text>
      {canRemoveParts ? <Box w="28px" /> : null}
    </HStack>

    <Box
      maxH="180px"
      overflowY="auto"
      pr={1}
      css={{
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: "#CBD5E0",
          borderRadius: "2px",
        },
      }}
    >
      <VStack align="stretch" spacing={2}>
        {parts.map((part, index) => {
          const cancelledQty = Math.max(
            0,
            (part.requestedQty ?? part.quantity) - part.quantity,
          );
          return (
          <HStack
            key={`${part.partNumber}-${index}`}
            spacing={4}
            pb={1}
            borderBottom="1px dashed"
            borderColor="gray.200"
            _last={{ borderBottom: "none" }}
          >
            <VStack align="start" spacing={0} flex="1">
              <Text
                fontSize="0.8rem"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="700"
                lineHeight="1"
              >
                {part.partNumber}
              </Text>
              <Text
                fontSize="0.8rem"
                color="gray.600"
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="500"
                mt="1"
                lineHeight="1.1"
                wordBreak="break-all"
              >
                {part.partName}
              </Text>
            </VStack>
            <VStack spacing={0} w="72px" align="flex-end">
              <Text
                fontSize="0.8rem"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
                textAlign="right"
                fontWeight="600"
              >
                {part.quantity}
              </Text>
              {cancelledQty > 0 ? (
                <Text
                  fontSize="0.65rem"
                  color="red.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  textAlign="right"
                  lineHeight="1.1"
                >
                  {cancelledQty} cancelled
                </Text>
              ) : null}
            </VStack>
            {canRemoveParts ? (
              <IconButton
                aria-label={`Remove part ${part.partNumber}`}
                icon={<FaRegTrashAlt size={14} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                minW="20px"
                w="20px"
                h="20px"
                p={0}
                cursor="pointer"
                isDisabled={isExpired || isReadOnly}
                onClick={() => onRemovePart?.(index)}
                _hover={{ color: "red.500", bg: "transparent" }}
                _active={{ bg: "transparent" }}
              />
            ) : null}
          </HStack>
          );
        })}
      </VStack>
    </Box>

    <SplitConfigurationSection
      key={`${splitId}-${transactionType ?? ""}-${discountPercentage ?? ""}-${creditNotePercentage ?? ""}-${fulfillmentStatus ?? ""}-${payableBy ?? ""}-${payableTo ?? ""}`}
      estimateNo={estimateNo}
      splitId={splitId}
      supplierId={supplierId}
      orderData={orderData}
      orderNo={orderNo}
      garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
      isSingleGaraazInvoiceRequired={isSingleGaraazInvoiceRequired}
      isRemainingItems={isRemainingItems}
      transactionType={transactionType}
      discountPercentage={discountPercentage}
      creditNotePercentage={creditNotePercentage}
      fulfillmentStatus={fulfillmentStatus}
      paymentRouteFromLabel={paymentRouteFromLabel}
      paymentRouteToLabel={paymentRouteToLabel}
      payableBy={payableBy}
      payableTo={payableTo}
      isExpired={isExpired}
      onSplitConfigApplied={onSplitConfigApplied}
      isReadOnly={isReadOnly}
    />
  </Box>
);
